
import React, { useState, useEffect } from 'react';
import { Product, ProductType } from '../types';
import Modal from './Modal';
import { usePreferences } from '../contexts/PreferencesContext';
import ProductDefectsSection, { ProductDefect } from './ProductDefectsSection';

interface InputFieldProps {
  label: string;
  name: string; // Using string for name as it covers different field types
  value: string | number;
  onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => void;
  type?: string;
  error?: string;
  required?: boolean;
  step?: string; // For number inputs
}

const InputField: React.FC<InputFieldProps> = ({ label, name, value, onChange, type = 'text', error, required, step }) => (
  <div className="mb-4">
    <label htmlFor={name} className="block text-sm font-medium text-slate-700 mb-1">
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    <input
      type={type}
      id={name}
      name={name}
      value={value}
      onChange={onChange}
      step={step}
      className={`w-full px-3 py-2 border rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm ${error ? 'border-red-500' : 'border-slate-300'}`}
      aria-required={required}
      aria-describedby={error ? `${name}-error` : undefined}
    />
    {error && <p id={`${name}-error`} className="mt-1 text-xs text-red-600">{error}</p>}
  </div>
);

// FIX: Define an interface for the form data to strongly type pieceWeight
interface ProductFormData {
  productId: string;
  name: string;
  pieceWeight: number | ''; // Allow pieceWeight to be a number or an empty string
  productType: ProductType;
  outfeedDisplayName: string; // Abbreviated display name for outfeed dashboard
  yieldDisplayName: string; // Abbreviated display name for yield calculations
}

interface ProductFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (product: Omit<Product, 'id'> | Product, isNew: boolean) => void;
  initialData?: Product | null;
  title: string;
  existingProductIds: string[]; // For uniqueness check
  defaultProductType?: ProductType; // Default product type for new products
}

const ProductFormModal: React.FC<ProductFormModalProps> = ({ isOpen, onClose, onSubmit, initialData, title, existingProductIds, defaultProductType = ProductType.INFEED }) => {
  const { weightUnit } = usePreferences();
  // FIX: Use ProductFormData interface and initialize pieceWeight as empty string
  const [formData, setFormData] = useState<ProductFormData>({
    productId: '',
    name: '',
    pieceWeight: '', // Initialize as empty string for better UX with number inputs
    productType: ProductType.INFEED, // Default to INFEED
    outfeedDisplayName: '',
    yieldDisplayName: '',
  });
  const [errors, setErrors] = useState({
    productId: '',
    name: '',
    pieceWeight: '',
    productType: '',
    outfeedDisplayName: '',
    yieldDisplayName: '',
  });
  const [pendingDefects, setPendingDefects] = useState<ProductDefect[]>([]);

  useEffect(() => {
    if (initialData) {
      setFormData({
        productId: initialData.productId,
        name: initialData.name,
        pieceWeight: initialData.pieceWeight, // This is a number from Product type
        productType: initialData.productType || ProductType.INFEED, // Default if missing
        outfeedDisplayName: initialData.outfeedDisplayName || '',
        yieldDisplayName: initialData.yieldDisplayName || '',
      });
    } else {
      // For new product, initialize with empty strings or suitable defaults
      setFormData({
        productId: '',
        name: '',
        pieceWeight: '', // Set to empty string for a new form
        productType: defaultProductType, // Use the default product type prop
        outfeedDisplayName: '',
        yieldDisplayName: '',
      });
    }
    setErrors({ productId: '', name: '', pieceWeight: '', productType: '', outfeedDisplayName: '', yieldDisplayName: '' });
  }, [isOpen, initialData, defaultProductType]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      // FIX: Store pieceWeight as number (or NaN from parseFloat) if not empty, else as empty string
      [name]: name === 'pieceWeight' ? (value === '' ? '' : parseFloat(value)) : value,
    }));
    setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const validate = (): boolean => {
    let isValid = true;
    const newErrors = { productId: '', name: '', pieceWeight: '', productType: '' };

    if (!formData.productId.trim()) {
      newErrors.productId = 'Product ID is required.';
      isValid = false;
    } else if (!/^[a-zA-Z0-9-_]+$/.test(formData.productId.trim())) {
      newErrors.productId = 'Product ID must be alphanumeric (can include - and _).';
      isValid = false;
    } else {
        const isProductIdTaken = existingProductIds.some(id => 
            id.toLowerCase() === formData.productId.trim().toLowerCase() &&
            (!initialData || initialData.productId.toLowerCase() !== formData.productId.trim().toLowerCase())
        );
        if (isProductIdTaken) {
            newErrors.productId = 'This Product ID is already taken.';
            isValid = false;
        }
    }


    if (!formData.name.trim()) {
      newErrors.name = 'Product Name is required.';
      isValid = false;
    }

    // FIX: Validation for pieceWeight (line 107). formData.pieceWeight is number | ''.
    // This comparison is now type-safe and correct.
    if (formData.pieceWeight === '' || isNaN(Number(formData.pieceWeight)) || Number(formData.pieceWeight) <= 0) {
      newErrors.pieceWeight = 'Average Piece Weight must be a positive number.';
      isValid = false;
    }
    
    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) {
      return;
    }
    // Ensure pieceWeight is definitely a number before submitting.
    // Number('') is 0, which is caught by validation (must be positive).
    // NaN is also caught by validation.
    const pieceWeightAsNumber = Number(formData.pieceWeight); 

    const finalFormData = {
        ...formData,
        pieceWeight: pieceWeightAsNumber
    };

    // Submit the product first
    if (initialData) {
      onSubmit({ ...initialData, ...finalFormData }, false);
    } else {
      onSubmit(finalFormData, true);
    }

    // If we have an ID (either existing or after creation) and there are defects to save
    if (formData.productType === ProductType.OUTFEED && pendingDefects.length > 0) {
      // Note: We'll need to handle defect saving after the product is created
      // For now, we'll store them and the parent component should handle them
      console.log('Defects to save:', pendingDefects);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={title}>
      <form onSubmit={handleSubmit} noValidate>
        <InputField
          label="Product ID"
          name="productId"
          value={formData.productId}
          onChange={handleChange}
          error={errors.productId}
          required
        />
        <InputField
          label="Product Name"
          name="name"
          value={formData.name}
          onChange={handleChange}
          error={errors.name}
          required
        />
        {/* Custom field for piece weight with unit in input */}
        <div className="mb-4">
          <label htmlFor="pieceWeight" className="block text-sm font-medium text-slate-700 mb-1">
            Average Piece Weight <span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <input
              type="number"
              id="pieceWeight"
              name="pieceWeight"
              value={formData.pieceWeight === '' ? '' : formData.pieceWeight.toString()}
              onChange={handleChange}
              step="any"
              className={`w-full px-3 py-2 pr-12 border rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm ${
                errors.pieceWeight ? 'border-red-500' : 'border-slate-300'
              }`}
              aria-required={true}
              aria-describedby={errors.pieceWeight ? 'pieceWeight-error' : undefined}
            />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-slate-500">
              {weightUnit === 'KGS' ? 'kg' : 'lbs'}
            </span>
          </div>
          {errors.pieceWeight && <p id="pieceWeight-error" className="mt-1 text-xs text-red-600">{errors.pieceWeight}</p>}
        </div>
        
        <div className="mb-4">
          <label htmlFor="productType" className="block text-sm font-medium text-slate-700 mb-1">
            Product Type <span className="text-red-500">*</span>
          </label>
          <select
            id="productType"
            name="productType"
            value={formData.productType}
            onChange={handleChange}
            disabled={!!initialData} // Disable when editing existing product
            className={`w-full px-3 py-2 border rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm ${errors.productType ? 'border-red-500' : 'border-slate-300'} ${!!initialData ? 'bg-slate-100 cursor-not-allowed' : ''}`}
            required
          >
            <option value={ProductType.INFEED}>Infeed</option>
            <option value={ProductType.OUTFEED}>Outfeed</option>
          </select>
          {errors.productType && <p className="mt-1 text-xs text-red-600">{errors.productType}</p>}
          {!!initialData && <p className="mt-1 text-xs text-slate-500">Product type cannot be changed after creation</p>}
        </div>

        {formData.productType === ProductType.OUTFEED && (
          <>
            <InputField
              label="Outfeed Display Name"
              name="outfeedDisplayName"
              value={formData.outfeedDisplayName}
              onChange={handleChange}
              error={errors.outfeedDisplayName}
            />
            <p className="text-xs text-slate-500 mb-4">Optional: Short name for outfeed dashboard (e.g., "OF", "Out")</p>

            <InputField
              label="Yield Display Name"
              name="yieldDisplayName"
              value={formData.yieldDisplayName}
              onChange={handleChange}
              error={errors.yieldDisplayName}
            />
            <p className="text-xs text-slate-500 mb-4">Optional: Short name for yield calculations (e.g., "Y%", "Yield")</p>
            
            {/* Defects Section for Outfeed Products */}
            <ProductDefectsSection
              productId={initialData?.id}
              isNewProduct={!initialData}
              onDefectsChange={setPendingDefects}
            />
          </>
        )}
        
        <div className="mt-6 flex justify-end space-x-3">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-slate-700 bg-slate-100 hover:bg-slate-200 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 border border-transparent rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors"
          >
            Save
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default ProductFormModal;
